import { Fragment, useEffect } from "react";
import { Box, useThemeUI } from "theme-ui";
import { useDispatch, useSelector } from "react-redux";
import omit from "lodash/fp/omit";
import castArray from "lodash/castArray";
import get from "lodash/fp/get";
import {
  selectIsAnalyticsEnabled,
  selectSelectedProduct,
  selectUIVersion,
} from "store/app";
import { trackElementChange } from "utils";

import {
  updateUserChoiceValues,
  displayInfoModal,
  setTouchedField,
} from "store/app/appSlice";
import { Button } from "components";
import { ChevronLeftIcon } from "components/icons";
import FieldsSelector from "./FieldsSelector";
import { conditions } from "utils/shouldBeDisplayed";

export function getIsDisabled({ isDisabledIf, userChoiceValues }) {
  return Object.keys(isDisabledIf).some((key) => {
    const { includes, notIncludes } = isDisabledIf[key];
    if (includes && castArray(includes).includes(userChoiceValues[key] || "")) {
      return true;
    }
    if (
      notIncludes &&
      castArray(notIncludes).includes(userChoiceValues[key] || "")
    ) {
      return true;
    }
    return false;
  });
}

export default function FieldsSelectorWrapper({
  customisationTitle,
  component,
  userChoices,
  userChoiceValues,
  name,
  isDisabled: isDisabledOverride,
  isDisabledIf = {},
  isRequired,
  width,
  defaultValueToSetOnRender,
  toolTip,

  // for <Checkbox />
  label,
  labelIcon,
  checkedValue,
  uncheckedValue,

  // for <Input />
  as,
  type,
  initialValue,
  maxLength,
  placeholder,
  hint,
  info,

  // For Buttons
  options = [],
  fields = [],

  customOrdering,

  hasOptionalFields,

  focusedField,
  setFocusedField,
  goBackFocusedFields,
  allowUnselect,
  onChange = () => {},

  resetButtonOption,
  goBackOnSelect,

  props = {},
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const UIVersion = useSelector(selectUIVersion);

  const dispatch = useDispatch();
  const selectedProduct = useSelector(selectSelectedProduct);
  const selectedProductId = selectedProduct?.id;

  const fieldsTouched = useSelector(get(["app", "fieldsTouched"]));

  const isAnalyticsEnabled = useSelector(selectIsAnalyticsEnabled);

  const dispatchSetTouchedField = (nameOveride) => {
    dispatch(setTouchedField(nameOveride || name));
  };

  const dispatchUpdateUserChoices = (value, nameOveride) => {
    const valuesToUpdate = [
      {
        name: nameOveride || name,
        value,
        // value: userChoiceValues[name] !== value ? value : null,
      },
    ];

    // clear "Holder Length" if one of these values changes (Skate)
    if (["size_left", "size_right", "Fit", "Blade Type"].includes(name)) {
      valuesToUpdate.push({
        name: "Holder Length",
        value: null,
      });
    }

    // PC GS 2026: Handle Length options depend on Graphic and Paddle Length
    if (
      name === "Paddle Length" &&
      selectedProductId === "ProCustomGoalieStick2026"
    ) {
      const currentHandleLength = userChoiceValues["Handle Length"];
      if (userChoiceValues["Graphic"] === "Supreme") {
        // "28" is only valid for Paddle 23; "32" is only valid for Paddle 24-27. Reset to "30" when the selection becomes invalid.
        if (
          (currentHandleLength === "28" && value !== "23") ||
          (currentHandleLength === "32" && value === "23")
        ) {
          valuesToUpdate.push({ name: "Handle Length", value: "30" });
        }
      } else {
        // Vapor: 28 and 30 are always valid — re-push current value to prevent downstream clearing
        valuesToUpdate.push({
          name: "Handle Length",
          value: currentHandleLength,
        });
      }
    }

    // clear "Eyelets" if "size_left", "size_right" is 4 or less, or there is 3/4 difference between the two (Skate)
    if (
      ["size_left", "size_right"].includes(name) &&
      userChoiceValues.Eyelets === "HyperFlex Eyelets" &&
      conditions.skateBootLengthCheck(userChoiceValues) &&
      conditions.skateBootLengthCheckForHolder(userChoiceValues)
    ) {
      valuesToUpdate.push({
        name: "Eyelets",
        value: null,
      });
    }

    if (
      ["size_left", "size_right"].includes(name) &&
      userChoiceValues.Holder === "Powerfly" &&
      conditions.skateBootLengthCheckForHolder(userChoiceValues)
    ) {
      valuesToUpdate.push({
        name: "Holder",
        value: "LS Edge",
      });
    }

    // QT Vapor Flylite Goalie Stick: JR Paddle Length invalidates Blue/Navy/Black
    // Shaft Color (resets to White); Logos/Paddle Color code mirror Shaft Color
    // but aren't covered by that reset, so update them here too.
    if (
      name === "Paddle Length" &&
      selectedProduct?.productFamily === "QuickTurnVaporFlyliteGoalieStick" &&
      ["22", "23"].includes(value) &&
      !["White", "Red"].includes(userChoiceValues["Shaft Color"])
    ) {
      valuesToUpdate.push(
        { name: "Logos Color code", value: "WHITE" },
        { name: "Paddle Color code", value: "WHITE" }
      );
    }

    // QT Fuse Goalie Stick: Paddle Length can invalidate Silver/White Color
    // Choice (resets to Blue); the six mirrored color-code fields aren't
    // covered by that reset, so update them to their Blue equivalents here.
    if (
      name === "Paddle Length" &&
      selectedProduct?.productFamily === "QuickTurnSupremeFuseGoalieStick" &&
      ((userChoiceValues["Color Choice"] === "Silver" &&
        !["24", "25", "26"].includes(value)) ||
        (userChoiceValues["Color Choice"] === "White" &&
          !["23", "24", "25", "26"].includes(value)))
    ) {
      valuesToUpdate.push(
        { name: "Paddle Color code", value: "Black" },
        { name: "Shoulder Color code", value: "Black" },
        { name: "Shaft Color code", value: "Blue" },
        { name: "Shoulder Decal Color code", value: "Blue" },
        { name: "Outline Color code", value: "Blue" },
        { name: "Spine Color code", value: "Blue" }
      );
    }

    // update userChoiceValues["Shoulder Decal Color code"] value if Goalie Shaft is changed from Black
    if (
      name === "Shaft Color code" &&
      userChoiceValues["Shaft Color code"] === "BLACK/BLACK"
    ) {
      valuesToUpdate.push({
        name: "Shoulder Decal Color code",
        value: "WHITE/RED",
      });
    }
    // update userChoiceValues["Shaft Color code"] value if Goalie Shoulder is changed from Black
    if (
      name === "Shoulder Decal Color code" &&
      userChoiceValues["Shoulder Decal Color code"] === "BLACK/BLACK"
    ) {
      valuesToUpdate.push({
        name: "Shaft Color code",
        value: "WHITE/RED",
      });
    }

    // ! DEV Changing t-trap from "double" to "single" removes t-spine-lace--glove as an option, changing it back "double" adds it back but it doesn't apply it's default value. Temporary fix here, be good to fix properly
    if (
      ["t-trap"].includes(name) &&
      ["(Not Vapor90) Double", "Double"].includes(value)
    ) {
      valuesToUpdate.push({
        name: "t-spine-lace--glove",
        value: "White",
      });
    }

    // If you need checkbox default to change based on another field, use this. Commented out as product defaults changed.
    // if (["glove-construction"].includes(name)) {
    //   valuesToUpdate.push({
    //     name: "t-trap-reinforced",
    //     value: value === "Vapor 90",
    //   });
    // }

    // Graphic is a hard reset for values, but shouldn't reset handedness
    if (["graphic"].includes(name)) {
      valuesToUpdate.push({
        name: "full-right-hand",
        value: userChoiceValues["full-right-hand"],
      });
    }

    if (
      name === "outside-zone-color" &&
      value === "Black" &&
      userChoiceValues["bauer-logo-color"] === "Black"
    ) {
      valuesToUpdate.push({
        name: "bauer-logo-color",
        value: "White",
      });
    }

    // ! DEV: Shadow Specific, can be removed once Fuse is live
    const shadowLogoFillColorGloveIsWhite =
      userChoiceValues["shadow-logo-fill-color--glove"] === "White" ||
      userChoiceValues["shadow-logo-fill-color"] === "White";
    if (
      name === "shadow-logo-outline-color--glove" &&
      value === "White" &&
      shadowLogoFillColorGloveIsWhite
    ) {
      valuesToUpdate.push({
        name: "shadow-logo-fill-color--glove",
        value: "Black",
      });
    }

    const shadowLogoFillColorBlockerIsWhite =
      userChoiceValues["shadow-logo-fill-color--blocker"] === "White" ||
      userChoiceValues["shadow-logo-fill-color"] === "White";
    if (
      name === "shadow-logo-outline-color--blocker" &&
      value === "White" &&
      shadowLogoFillColorBlockerIsWhite
    ) {
      valuesToUpdate.push({
        name: "shadow-logo-fill-color--blocker",
        value: "Black",
      });
    }

    // Track analytics event for the first value being updated
    if (isAnalyticsEnabled && valuesToUpdate.length > 0) {
      const elementType = `${nameOveride || name}${
        component === "Checkbox" ? `-(${label})` : ""
      }`;
      trackElementChange({
        elementType,
        elementValue: valuesToUpdate[0].value,
        debounce: type === "text" || as === "input",
      });
    }

    dispatch(updateUserChoiceValues(valuesToUpdate));
    onChange(valuesToUpdate);
  };

  //Goalie Stick Hyperlite2 Shaft and Shoulder relation
  useEffect(() => {
    if (userChoiceValues["Shaft Color code"] === "BLACK/BLACK") {
      dispatch(
        updateUserChoiceValues({
          name: "Shoulder Decal Color code",
          value: "BLACK/BLACK",
        })
      );
    }
  }, [userChoiceValues["Shaft Color code"]]);

  useEffect(() => {
    if (userChoiceValues["Shoulder Decal Color code"] === "BLACK/BLACK") {
      dispatch(
        updateUserChoiceValues({
          name: "Shaft Color code",
          value: "BLACK/BLACK",
        })
      );
    }
  }, [userChoiceValues["Shoulder Decal Color code"]]);

  const isGoalieGearProduct = [
    "VaporFlyliteGoalieGear2025",
    "VaporFlyliteGoalieGear2026",
    "SupremeFuseGoalieGear2026",
    "ProCustomGoalierGear2023",
    "SVProGoalieGear2026",
  ].includes(selectedProductId);

  const isSVPro = selectedProductId === "SVProGoalieGear2026";

  const getZoneColor = (zone, piece) => {
    return userChoiceValues[`${zone}--${piece}`] || userChoiceValues[zone];
  };

  // Zone colors where a Black logo should auto-switch to White.
  // Read from the "bauer-logo-color" field's Black option displayIf.notIncludes
  // in the "colors" customisation. Falls back to ["Black"] if not found.
  const getDarkZoneColors = () => {
    const colorsCustomisation = selectedProduct?.customisations?.find(
      (c) => c.name === "colors"
    );
    const blackLogoOption = colorsCustomisation?.fields
      ?.flatMap((f) => f.fields ?? [])
      .find((f) => f.name === "bauer-logo-color")
      ?.options?.find((o) => o.value === "Black");

    const displayIf = blackLogoOption?.displayIf;
    const displayIfArray = Array.isArray(displayIf)
      ? displayIf
      : [displayIf ?? {}];

    return (
      displayIfArray
        .flatMap((c) => Object.values(c ?? {}))
        .find((v) => v?.notIncludes)?.notIncludes ?? ["Black"]
    );
  };

  const darkZoneColors = getDarkZoneColors();

  const getContrastLogoColor = (zoneColors, currentLogoColor) => {
    if (!currentLogoColor) return null;
    const colors = Array.isArray(zoneColors) ? zoneColors : [zoneColors];
    if (
      currentLogoColor === "Black" &&
      colors.some((c) => darkZoneColors.includes(c))
    ) {
      return "White";
    }
    if (currentLogoColor === "White" && colors.includes("White")) {
      return "Black";
    }
    return null;
  };

  // Glove — Bauer logo
  useEffect(() => {
    if (!isGoalieGearProduct) return;
    const zoneColor = isSVPro
      ? getZoneColor("outer-accent-color", "glove")
      : getZoneColor("outside-zone-color", "glove") ||
        getZoneColor("zone-1-color", "glove");
    const contrast = getContrastLogoColor(
      zoneColor,
      getZoneColor("bauer-logo-color", "glove")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "bauer-logo-color--glove",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["outside-zone-color--glove"],
    userChoiceValues["outside-zone-color"],
    userChoiceValues["zone-1-color--glove"],
    userChoiceValues["zone-1-color"],
    userChoiceValues["outer-accent-color--glove"],
    userChoiceValues["outer-accent-color"],
  ]);

  // Glove — SVPro logo
  useEffect(() => {
    if (!isGoalieGearProduct || !isSVPro) return;
    const zoneColor = getZoneColor("bottom-zone-color", "glove");
    const contrast = getContrastLogoColor(
      zoneColor,
      getZoneColor("svpro-logo-color", "glove")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "svpro-logo-color--glove",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["bottom-zone-color--glove"],
    userChoiceValues["bottom-zone-color"],
  ]);

  // Glove — Hyp2rlite logo
  useEffect(() => {
    if (!isGoalieGearProduct || isSVPro) return;
    const zoneColors = [
      getZoneColor("outside-bottom-zone-color", "glove"),
      getZoneColor("zone-3-color", "glove"),
    ];
    const contrast = getContrastLogoColor(
      zoneColors,
      getZoneColor("hyp2rlite-logo-color", "glove")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "hyp2rlite-logo-color--glove",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["outside-bottom-zone-color--glove"],
    userChoiceValues["outside-bottom-zone-color"],
    userChoiceValues["zone-3-color--glove"],
    userChoiceValues["zone-3-color"],
  ]);

  // Glove — Supreme brand logo
  useEffect(() => {
    if (!isGoalieGearProduct || isSVPro) return;
    const zoneColor = getZoneColor("zone-6-color", "glove");
    const contrast = getContrastLogoColor(
      zoneColor,
      getZoneColor("supreme-model-logo-color", "glove")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "supreme-model-logo-color--glove",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["zone-6-color--glove"],
    userChoiceValues["zone-6-color"],
  ]);

  // Blocker — Bauer logo
  useEffect(() => {
    if (!isGoalieGearProduct) return;
    const zoneColor = isSVPro
      ? getZoneColor("outer-accent-color", "blocker")
      : getZoneColor("outside-zone-color", "blocker") ||
        getZoneColor("zone-1-color", "blocker");
    const contrast = getContrastLogoColor(
      zoneColor,
      getZoneColor("bauer-logo-color", "blocker")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "bauer-logo-color--blocker",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["outside-zone-color--blocker"],
    userChoiceValues["outside-zone-color"],
    userChoiceValues["zone-1-color--blocker"],
    userChoiceValues["zone-1-color"],
    userChoiceValues["outer-accent-color--blocker"],
    userChoiceValues["outer-accent-color"],
  ]);

  // Blocker — SVPro logo
  useEffect(() => {
    if (!isGoalieGearProduct || !isSVPro) return;
    const zoneColor = getZoneColor("bottom-zone-color", "blocker");
    const contrast = getContrastLogoColor(
      zoneColor,
      getZoneColor("svpro-logo-color", "blocker")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "svpro-logo-color--blocker",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["bottom-zone-color--blocker"],
    userChoiceValues["bottom-zone-color"],
  ]);

  // Blocker — Hyp2rlite logo
  useEffect(() => {
    if (!isGoalieGearProduct || isSVPro) return;
    const zoneColors = [
      getZoneColor("outside-bottom-zone-color", "blocker"),
      getZoneColor("zone-3-color", "blocker"),
    ];
    const contrast = getContrastLogoColor(
      zoneColors,
      getZoneColor("hyp2rlite-logo-color", "blocker")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "hyp2rlite-logo-color--blocker",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["outside-bottom-zone-color--blocker"],
    userChoiceValues["outside-bottom-zone-color"],
    userChoiceValues["zone-3-color--blocker"],
    userChoiceValues["zone-3-color"],
  ]);

  // Blocker — Supreme logo
  useEffect(() => {
    if (!isGoalieGearProduct || isSVPro) return;
    const zoneColor = getZoneColor("zone-8-color", "blocker");
    const contrast = getContrastLogoColor(
      zoneColor,
      getZoneColor("supreme-logo-color", "blocker")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "supreme-logo-color--blocker",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["zone-8-color--blocker"],
    userChoiceValues["zone-8-color"],
  ]);

  // Blocker — Supreme brand logo
  useEffect(() => {
    if (!isGoalieGearProduct || isSVPro) return;
    const zoneColor = getZoneColor("zone-6-color", "blocker");
    const contrast = getContrastLogoColor(
      zoneColor,
      getZoneColor("supreme-model-logo-color", "blocker")
    );
    if (contrast) {
      dispatch(
        updateUserChoiceValues({
          name: "supreme-model-logo-color--blocker",
          value: contrast,
        })
      );
    }
  }, [
    userChoiceValues["zone-6-color--blocker"],
    userChoiceValues["zone-6-color"],
  ]);

  const selectedOption = options.find(
    (option) => option.value === userChoiceValues[name]
  );

  const subFields = selectedOption?.fields || [];
  const hasSubFields = subFields.length > 0;
  const renderSelectedOptionForSubFields =
    selectedOption &&
    selectedOption.hasOwnProperty("renderSelectedOptionForSubFields")
      ? selectedOption.renderSelectedOptionForSubFields
      : true;

  const optionsToRender =
    hasSubFields && !renderSelectedOptionForSubFields
      ? []
      : (hasSubFields ? [selectedOption].map(omit("hint")) : options).map(
          ({ isDisabledIf, ...option }) => {
            return {
              ...option,
              isDisabled:
                isDisabledIf &&
                getIsDisabled({ isDisabledIf, userChoiceValues }),
            };
          }
        );
  // conditional render selected option for hasSubFields, renderSelectedOptionForSubFields

  const orderedOptionsToRender = customOrdering
    ? (() => {
        const orderedOptions = [];
        const usedIndices = new Set();

        // Add options in the custom order, accounts for intended duplicate values
        customOrdering.forEach((value) => {
          optionsToRender.forEach((option, index) => {
            if (option.value === value && !usedIndices.has(index)) {
              orderedOptions.push(option);
              usedIndices.add(index);
            }
          });
        });

        // Add remaining options that weren't included
        optionsToRender.forEach((option, index) => {
          if (!usedIndices.has(index)) {
            orderedOptions.push(option);
          }
        });

        return orderedOptions;
      })()
    : optionsToRender;

  const isDisabled =
    isDisabledOverride !== undefined
      ? isDisabledOverride
      : getIsDisabled({ isDisabledIf, userChoiceValues });

  const goBack = () => {
    dispatchUpdateUserChoices(null);
    subFields.forEach((subField) => {
      dispatch(
        updateUserChoiceValues({
          name: subField.name,
          value: null,
        })
      );
    });
  };

  const displayInfo = info
    ? () => {
        dispatch(displayInfoModal(info));
      }
    : null;

  return (
    <>
      {hasSubFields && (
        <Button
          variant="secondary"
          size={isMobile ? "medium" : "large"}
          iconBefore={<ChevronLeftIcon />}
          aria-label="go back"
          isRound
          onClick={goBack}
        >
          {isMobile && "GO BACK"}
        </Button>
      )}

      <FieldsSelector
        customisationTitle={customisationTitle}
        component={component}
        userChoices={userChoices}
        userChoiceValues={userChoiceValues}
        name={name}
        isDisabled={isDisabled}
        isRequired={isRequired}
        isFieldTouched={fieldsTouched.includes(name)}
        setTouchedField={dispatchSetTouchedField}
        width={width}
        // for <Checkbox />
        label={label}
        labelIcon={labelIcon}
        checkedValue={checkedValue}
        uncheckedValue={uncheckedValue}
        // for <Input />
        as={as}
        type={type}
        initialValue={initialValue}
        maxLength={maxLength}
        placeholder={placeholder}
        hint={hint}
        displayInfo={displayInfo}
        // For Buttons
        options={orderedOptionsToRender}
        fields={fields}
        onChange={dispatchUpdateUserChoices}
        focusedField={focusedField}
        setFocusedField={setFocusedField}
        goBackFocusedFields={goBackFocusedFields}
        allowUnselect={allowUnselect}
        hasOptionalFields={hasOptionalFields}
        defaultValueToSetOnRender={defaultValueToSetOnRender}
        toolTip={toolTip}
        props={props}
        resetButtonOption={resetButtonOption}
        goBackOnSelect={goBackOnSelect}
      />

      {subFields.map((field) => (
        <Fragment key={field.name}>
          {field?.dividerLeft && (
            <Box
              sx={{
                width: UIVersion === 2 ? "2px" : isMobile ? "100%" : "2px",
                height: UIVersion === 2 ? "60px" : isMobile ? "2px" : "60px",
                background: "#D9D9D9",
              }}
            />
          )}

          <FieldsSelectorWrapper
            customisationTitle={customisationTitle}
            component={field.component}
            userChoices={userChoices}
            userChoiceValues={userChoiceValues}
            name={field.name}
            placeholder={field.placeholder}
            hint={field.hint}
            as={field.as}
            type={field.type}
            initialValue={field.initialValue}
            maxLength={field.maxLength}
            isRequired={field.isRequired}
            options={field.options}
            fields={fields}
            label={field.label}
            labelIcon={field.labelIcon}
            checkedValue={field.checkedValue}
            uncheckedValue={field.uncheckedValue}
            isDisabledIf={field.isDisabledIf}
            width={field.width}
            defaultValueToSetOnRender={field.defaultValueToSetOnRender}
            toolTip={field.toolTip}
            props={field.props}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            goBackFocusedFields={goBackFocusedFields}
          />
        </Fragment>
      ))}
    </>
  );
}
